import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { AppLayout } from "../shared/layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { CreateHabitDialog } from "@/components/habits/create-habit-dialog";
import { HabitCard } from "@/components/habits/habit-card";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getHabits, deleteHabit, checkInHabit } from "@/data/requests/habit-requests";
import { Plus, Target, TrendingUp, Flame } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { CardGridLoader } from "@/components/shared/page-loader";
import { Progress } from "@/components/ui/progress";

export function HabitsScreen() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [editingHabit, setEditingHabit] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  // Fetch habits
  const { data: habitsResponse, isLoading } = useQuery({
    queryKey: ["habits"],
    queryFn: getHabits,
  });

  const habits = habitsResponse?.data || [];

  // Calculate overall stats
  const totalHabits = habits.length;
  const activeStreak = Math.max(...habits.map(h => h.streak || 0), 0);
  const avgCompletion = habits.length > 0 
    ? habits.reduce((sum, h) => sum + (h.completionRate || 0), 0) / habits.length 
    : 0;

  // Check-in mutation
  const checkInMutation = useMutation({
    mutationFn: ({ id, note }) => checkInHabit(id, { note }),
    onSuccess: async () => {
      // Invalidate and refetch immediately
      await queryClient.invalidateQueries({ queryKey: ["habits"] });
      await queryClient.refetchQueries({ queryKey: ["habits"] });
      toast({
        title: t("habits.checkedIn"),
        description: t("habits.keepStreak"),
      });
    },
    onError: (error) => {
      const message = error.response?.data?.message || t("habits.checkInFailed");
      toast({
        title: t("common.error"),
        description: message,
        variant: "destructive",
      });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: deleteHabit,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["habits"] });
      toast({
        title: t("habits.habitDeleted"),
        description: t("habits.habitDeletedDesc"),
      });
    },
    onError: () => {
      toast({
        title: t("common.error"),
        description: t("habits.deleteFailed"),
        variant: "destructive",
      });
    },
  });

  const handleCheckIn = (habitId) => {
    checkInMutation.mutate({ id: habitId, note: "" });
  };

  const handleEdit = (habit) => {
    setEditingHabit(habit);
  };

  const handleDelete = (habitId) => {
    if (window.confirm(t("habits.confirmDelete"))) {
      deleteMutation.mutate(habitId);
    }
  };

  if (isLoading) {
    return (
      <AppLayout title={t("habits.title")} description={t("habits.loading")}>
        <CardGridLoader count={6} />
      </AppLayout>
    );
  }

  return (
    <AppLayout
      title={t("habits.title")}
      description={t("habits.subtitle")}
      actions={[
        {
          name: t("habits.newHabit"),
          icon: <Plus className="h-4 w-4" />,
          onClick: () => setShowCreateDialog(true),
        },
      ]}
    >
      <div className="space-y-6 max-w-6xl mx-auto">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {t("habits.totalHabits")}
                </CardTitle>
                <Target className="h-4 w-4 text-primary" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalHabits}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {t("habits.longestStreak")}
                </CardTitle>
                <Flame className="h-4 w-4 text-orange-500" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{activeStreak} {t("habits.days")}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {t("habits.avgCompletion")}
                </CardTitle>
                <TrendingUp className="h-4 w-4 text-green-500" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{Math.round(avgCompletion)}%</div>
              <Progress value={avgCompletion} className="mt-2" />
            </CardContent>
          </Card>
        </div>

        {/* Habits Grid */}
        {habits.length === 0 ? (
          <Card>
            <CardContent className="py-12">
              <div className="flex flex-col items-center justify-center text-center space-y-4">
                <Target className="h-12 w-12 text-muted-foreground/50" />
                <div>
                  <h3 className="text-lg font-semibold">{t("habits.noHabitsYet")}</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t("habits.startBuilding")}
                  </p>
                </div>
                <CreateHabitDialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                  <Button onClick={() => setShowCreateDialog(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    {t("habits.createFirstHabit")}
                  </Button>
                </CreateHabitDialog>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {habits.map((habit) => (
              <HabitCard
                key={habit.id}
                habit={habit}
                onCheckIn={() => handleCheckIn(habit.id)}
                onEdit={() => handleEdit(habit)}
                onDelete={() => handleDelete(habit.id)}
              />
            ))}
          </div>
        )}

        {/* Create/Edit Habit Dialog */}
        <CreateHabitDialog 
          open={showCreateDialog || !!editingHabit} 
          onOpenChange={(open) => {
            if (!open) {
              setShowCreateDialog(false);
              setEditingHabit(null);
            } else {
              setShowCreateDialog(true);
            }
          }}
          editHabit={editingHabit} 
          onClose={() => {
            setShowCreateDialog(false);
            setEditingHabit(null);
          }}
        >
          <button className="hidden" />
        </CreateHabitDialog>
      </div>
    </AppLayout>
  );
}
